import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import TeamV2CardComponent from './TeamV2CardComponent';

import { getCardsByUser } from 'apis/userApi';
import { useContactLinkContext } from 'contexts/ContactLinkContext'; // Add ContactLinkContext

const TeamV2CardContainer = ({ user, onCreateCardClick, onEditCardClick, searchQuery, items = [], selectedCards = new Set(), onCardSelection = () => { } }) => {
    const [cards, setCards] = useState([]);
    const [initialFetchComplete, setInitialFetchComplete] = useState(false);
    // const { userId } = user; // commented out: can crash when user is undefined
    const userId = user?.userId; // safe access

    // FIXED: Add ContactLinkContext to refresh contact links
    const { fetchContactLinksForCard, clearFetchedCards } = useContactLinkContext();

    // ✅ FIXED: Enhanced fetchCards function with better error handling
    const fetchUserCards = async (isRetry = false) => {
        try {
            if (isRetry) {
                console.log('🔍 CardsContainer: Retry fetching cards for userId:', userId);
            } else {
                console.log('🔍 CardsContainer: Fetching cards for userId:', userId);
            }

            const userCards = await getCardsByUser(userId);
            console.log('🔍 CardsContainer: API response:', userCards);

            if (Array.isArray(userCards)) {
                setCards(userCards);

                // FIXED: Check for transfer completion and refresh contact links
                const transferCompleted = localStorage.getItem('transferCompleted');
                const newCardId = localStorage.getItem('newlyCreatedCardId');

                if (transferCompleted && newCardId) {
                    console.log('🔍 CardsContainer: Transfer completed, refreshing contact links for new card:', newCardId);

                    // Clear any cached fetch state
                    clearFetchedCards();

                    // Force refresh contact links for the new card after a delay
                    setTimeout(async () => {
                        try {
                            await fetchContactLinksForCard(newCardId);
                            console.log('🔍 CardsContainer: Contact links refreshed for new card');
                        } catch (error) {
                            console.error('🔍 CardsContainer: Error refreshing contact links:', error);
                        }
                    }, 1000);

                    // Clear the flags
                    localStorage.removeItem('transferCompleted');
                    localStorage.removeItem('newlyCreatedCardId');
                }

                return userCards;
            } else {
                console.error('🔍 CardsContainer: Unexpected API response structure:', userCards);
                setCards([]);
                return [];
            }
        } catch (error) {
            console.error('🔍 CardsContainer: Error fetching cards:', error);
            setCards([]);
            return [];
        }
    };

    useEffect(() => {
        const initializeCards = async () => {
            // Check for newly created card in localStorage first
            const cardData = localStorage.getItem('cardData');
            if (cardData) {
                try {
                    const parsedCardData = JSON.parse(cardData);
                    console.log('🔍 CardsContainer: Found cardData in localStorage:', parsedCardData);

                    // Look for cards that belong to this user
                    const localCards = Object.values(parsedCardData).filter(card =>
                        card.cardUserId === userId && card.cardId && !card.cardId.includes('-') // Exclude temporary cards
                    );

                    if (localCards.length > 0) {
                        console.log('🔍 CardsContainer: Found local cards for user:', localCards);
                        // Add these cards to the state immediately
                        setCards(prevCards => {
                            const existingCardIds = new Set(prevCards.map(card => card._id));
                            const newCards = localCards.filter(card => !existingCardIds.has(card.cardId));
                            return [...prevCards, ...newCards];
                        });
                    }
                } catch (error) {
                    console.error('🔍 CardsContainer: Error parsing cardData:', error);
                }
            }

            // Fetch cards from API
            const fetchedCards = await fetchUserCards();
            setInitialFetchComplete(true);

            // ✅ FIXED: Only retry if we got no cards and haven't had a successful fetch yet
            if (fetchedCards.length === 0 && !initialFetchComplete) {
                console.log('🔍 CardsContainer: No cards found, scheduling single retry in 2 seconds');
                setTimeout(async () => {
                    const retryCards = await fetchUserCards(true);
                    if (retryCards.length === 0) {
                        console.log('🔍 CardsContainer: Retry completed, no additional cards found');
                    }
                }, 2000);
            }
        };

        // Only fetch when no static items provided and userId exists
        if (!items.length && userId) {
            initializeCards();
        }
    }, [items.length, userId, fetchContactLinksForCard, clearFetchedCards]); // ✅ FIXED: Proper dependencies

    // FIXED: Enhanced card click handler to refresh contact links
    const handleCardClick = async (card) => {
        try {
            console.log('🔍 CardsContainer: Card clicked:', card);

            // Clear any cached fetch state
            clearFetchedCards();

            // Force refresh contact links for this card
            await fetchContactLinksForCard(card._id || card.cardId);

            // Call the original edit handler
            onEditCardClick(card);

        } catch (error) {
            console.error('🔍 CardsContainer: Error handling card click:', error);
            // Still call the edit handler even if refresh fails
            onEditCardClick(card);
        }
    };

    // ✅ FIXED: Enhanced filtering with better null safety
    const filteredCards = cards.filter(card => {
        if (!card) return false;

        const fullName = card.contact_info?.fullName || card.fullName || '';
        const cardName = card.cardName || '';
        const searchText = (fullName + ' ' + cardName).toLowerCase();

        return searchQuery
            ? searchText.includes(searchQuery.toLowerCase())
            : true;
    });

    console.log('🔍 CardsContainer: Filtered cards:', filteredCards);

    // If items are provided, render static members instead of user cards
    if (Array.isArray(items) && items.length > 0) {
        const normalizedQuery = (searchQuery || '').toLowerCase();
        const filteredMembers = normalizedQuery
            ? items.filter((m) => {
                const name = `${m?.firstName || ''} ${m?.lastName || ''}`.trim().toLowerCase();
                const email = (m?.email || '').toLowerCase();
                return name.includes(normalizedQuery) || email.includes(normalizedQuery);
            })
            : items;

        return (
            <Box sx={{ padding: '1rem', width: '100%', boxSizing: 'border-box' }}>
                {filteredMembers.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No members found.</Typography>
                ) : (
                    <Grid container spacing={2}>
                        {filteredMembers.map((member) => {
                            const cardLike = {
                                _id: member.id || member._id,
                                cardName: `${member.firstName || ''} ${member.lastName || ''}`.trim(),
                                contact_info: {
                                    fullName: `${member.firstName || ''} ${member.lastName || ''}`.trim(),
                                    jobTitle: member.jobTitle || '',
                                    company: member.company || '',
                                    email: member.email || ''
                                },
                                email: member.email || '',
                                // Include metrics for the card component
                                metrics: {
                                    views: typeof member.views === 'number' ? member.views : 0,
                                    leads: typeof member.leads === 'number' ? member.leads : 0,
                                    devices: typeof member.devices === 'number' ? member.devices : 0
                                },
                                // Also add top-level fallbacks
                                views: typeof member.views === 'number' ? member.views : 0,
                                leads: typeof member.leads === 'number' ? member.leads : 0,
                                devices: typeof member.devices === 'number' ? member.devices : 0,
                                profilePictureUrl: member.avatarUrl || member.profilePictureUrl || null,
                                coverPhotoUrl: member.coverPhotoUrl || null,
                                companyLogoUrl: member.companyLogoUrl || null,
                                cardTheme: member.cardTheme || ''
                            };
                            return (
                                <TeamV2CardComponent
                                    key={cardLike._id || `${cardLike.contact_info.fullName}-${member.email || Math.random()}`}
                                    onEditCardClick={() => { }}
                                    cardName={cardLike.cardName}
                                    card={cardLike}
                                    isSelected={selectedCards.has(member.id)}
                                    onSelectionChange={(isSelected) => onCardSelection(member.id, isSelected)}
                                />
                            );
                        })}
                    </Grid>
                )}
            </Box>
        );
    }

    return (
        <Box
            sx={{
                padding: '1rem',
                width: '100%',
                boxSizing: 'border-box',
            }}
        >
            <Grid container spacing={2}>
                {filteredCards.map((card) => (
                    <TeamV2CardComponent
                        key={card._id || card.cardId}
                        onEditCardClick={() => handleCardClick(card)} // FIXED: Use enhanced handler
                        cardName={card.cardName || card.fullName}
                        card={card}
                    />
                ))}

            </Grid>
        </Box>
    );
};

export default TeamV2CardContainer;